/**
 * Doughrise AI Dough Coach - Final Working Version
 * Enhanced with target bake time scheduling and cold proof support
 */

(function () {
  const cfg = window.DoughriseCoach || {};
  const endpoint = cfg.endpoint;
  if (!endpoint) {
    console.error('DoughriseCoach: No endpoint configured');
    return;
  }

  const sessionKey = "dr_coach_session";
  const sessionId = localStorage.getItem(sessionKey) || crypto.randomUUID();
  localStorage.setItem(sessionKey, sessionId);

  console.log('Doughrise Coach loaded, session:', sessionId);

  let isLoading = false;
  var userMessageCount = 0;

  // ============================================================================
  // INJECT STYLES
  // ============================================================================
  
  const styles = document.createElement('style');
  styles.textContent = `
/* P2-09: no font @import. Inter and Newsreader already load sitewide; Fraunces/DM Sans were the retired brand fonts and cost a render-blocking request plus ~37 KB on every page. */

#dr-coach-trigger {
  position: fixed; bottom: 24px; right: 18px; z-index: 99999;
  background: #1A1A1A; border: 1px solid #333; border-radius: 50px;
  padding: 12px 20px; cursor: pointer;
  font-family: var(--dr-font-heading, 'Inter', system-ui, sans-serif); color: #DEA51B; font-size: 15px; font-weight: 600;
  box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  display: flex; align-items: center; gap: 8px;
  transition: box-shadow 0.2s;
}
#dr-coach-trigger:hover { box-shadow: 0 0 0 3px rgba(222,165,27,0.2), 0 4px 20px rgba(0,0,0,0.4); }

#dr-coach-modal {
  position: fixed; bottom: 80px; right: 18px; width: 380px;
  max-width: calc(100vw - 36px); height: 560px; max-height: calc(100vh - 120px);
  background: #1A1A1A; border-radius: 16px; box-shadow: 0 8px 40px rgba(0,0,0,0.6);
  display: flex; flex-direction: column; z-index: 99998;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; overflow: hidden;
}
#dr-coach-header {
  background: #1A1A1A; border-bottom: 1px solid #333;
  padding: 13px 16px; display: flex; align-items: center; gap: 10px;
  border-radius: 16px 16px 0 0; flex-shrink: 0;
}
.dr-coach-dot { width: 8px; height: 8px; border-radius: 50%; background: #DEA51B; flex-shrink: 0; }
#dr-coach-title { font-family: var(--dr-font-heading, 'Inter', system-ui, sans-serif); color: #DEA51B; font-size: 14px; font-weight: 600; }
#dr-coach-label { font-size: 10px; color: #666; margin-left: auto; }
#dr-coach-close {
  background: transparent; border: none; color: #888; font-size: 22px; cursor: pointer;
  padding: 0; width: 32px; height: 32px; min-width: 44px; min-height: 44px;
  display: flex; align-items: center; justify-content: center; border-radius: 4px;
}
#dr-coach-close:hover { color: #f0f0f0; background: rgba(255,255,255,0.05); }

#dr-coach-messages {
  flex: 1; overflow-y: auto; overflow-x: hidden; padding: 16px; background: #1A1A1A;
  display: flex; flex-direction: column; gap: 10px; scroll-behavior: smooth;
}
#dr-coach-messages::-webkit-scrollbar { width: 5px; }
#dr-coach-messages::-webkit-scrollbar-track { background: transparent; }
#dr-coach-messages::-webkit-scrollbar-thumb { background: #333; border-radius: 3px; }

.dr-msg { display: flex; margin-bottom: 4px; }
.dr-msg.dr-user { justify-content: flex-end; }
.dr-msg-content {
  padding: 10px 14px; max-width: 85%; word-wrap: break-word; line-height: 1.5; font-size: 13px;
}
.dr-msg.dr-assistant .dr-msg-content {
  background: #2a2a2a; border: 1px solid #333; color: #e0e0e0;
  border-radius: 10px 10px 10px 2px;
}
.dr-msg.dr-user .dr-msg-content {
  background: #DEA51B; color: #1A1A1A; font-weight: 600;
  border-radius: 10px 10px 2px 10px;
}

.dr-typing { display: flex; gap: 5px; padding: 4px 2px; }
.dr-typing span {
  width: 8px; height: 8px; border-radius: 50%; background: #DEA51B;
  animation: typing-bounce 1.4s infinite;
}
.dr-typing span:nth-child(2) { animation-delay: 0.2s; }
.dr-typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typing-bounce { 0%, 60%, 100% { transform: translateY(0); opacity: 0.6; } 30% { transform: translateY(-8px); opacity: 1; } }

#dr-coach-footer {
  padding: 8px 10px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  background: #111; border-top: 1px solid #2a2a2a;
  display: flex; gap: 8px; align-items: flex-end; border-radius: 0 0 16px 16px; flex-shrink: 0;
}
#dr-coach-input {
  flex: 1; background: #1e1e1e; border: 1px solid #333; border-radius: 20px;
  padding: 9px 14px; font-size: 13px; font-family: inherit; color: #f0f0f0;
}
#dr-coach-input::placeholder { color: #555; }
#dr-coach-input:focus { outline: none; border-color: #DEA51B44; }
#dr-coach-send {
  background: #DEA51B; border: none; border-radius: 50%;
  width: 36px; height: 36px; min-width: 44px; min-height: 44px;
  color: #1A1A1A; font-weight: 700; cursor: pointer; font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  transition: opacity 0.2s; flex-shrink: 0;
}
#dr-coach-send:hover { opacity: 0.85; }
#dr-coach-send:disabled { opacity: 0.4; cursor: not-allowed; }

.dr-bake-plan { background: #2a2a2a; border: 1px solid #333; border-radius: 12px; overflow: visible; margin: 8px 0; width: 100%; box-sizing: border-box; display: flex; flex-direction: column; }
.dr-bake-plan * { box-sizing: border-box; }
.dr-bake-plan-header { background: linear-gradient(135deg, #2a2000 0%, #3a2d00 100%); color: #f0f0f0; padding: 14px 18px; flex-shrink: 0; border-radius: 12px 12px 0 0; border-bottom: 1px solid #DEA51B44; }
.dr-plan-title { font-family: var(--dr-font-heading, 'Inter', system-ui, sans-serif); font-size: 15px; font-weight: 700; color: #DEA51B; margin-bottom: 6px; }
.dr-plan-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
.dr-meta-tag { background: rgba(222,165,27,0.15); color: #DEA51B; padding: 3px 8px; border-radius: 10px; font-size: 11px; border: 1px solid #DEA51B33; }
.dr-schedule-summary { background: #1c1700; color: #f0e0a0; padding: 12px 18px; font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 10px; border-top: 1px solid #2a2000; }
.dr-schedule-icon { font-size: 18px; }
.dr-cold-proof-badge { background: #0c1a2e; color: #93c5fd; padding: 10px 16px; font-size: 12px; display: flex; align-items: center; gap: 8px; border-top: 1px solid #1e3a5f; }
.dr-cold-icon { font-size: 16px; }
.dr-plan-ingredients { background: #222; padding: 14px 18px; border-bottom: 1px solid #333; width: 100%; flex-shrink: 0; }
.dr-ing-title { font-weight: 700; font-size: 13px; margin-bottom: 10px; color: #DEA51B; }
.dr-ing-list { font-size: 12px; color: #b0b0b0; line-height: 2; width: 100%; }
.dr-ing-list div { padding: 3px 0; word-wrap: break-word; }
.dr-bake-plan-assumptions { background: #221a00; border-left: 3px solid #DEA51B; padding: 10px 14px; font-size: 12px; color: #b8a060; flex-shrink: 0; }
.dr-bake-plan-timeline { padding: 16px 18px; width: 100%; overflow: visible; flex-shrink: 0; }
.dr-timeline-event { display: flex; gap: 14px; margin-bottom: 20px; position: relative; width: 100%; }
.dr-timeline-event::before { content: ''; position: absolute; left: 70px; top: 22px; bottom: -16px; width: 2px; background: linear-gradient(180deg, #333 0%, #1A1A1A 100%); }
.dr-timeline-event:last-child::before { display: none; }
.dr-event-time { min-width: 70px; font-weight: 700; color: #888; font-size: 12px; position: relative; line-height: 1.3; }
.dr-event-time .dr-clock-time { display: block; font-size: 13px; font-weight: 700; color: #f0f0f0; }
.dr-event-time .dr-offset-time { display: block; font-size: 10px; color: #555; margin-top: 2px; }
.dr-event-time::after { content: ''; position: absolute; right: -18px; top: 5px; width: 10px; height: 10px; background: #DEA51B; border-radius: 50%; border: 2px solid #1A1A1A; }
.dr-event-content { flex: 1; padding-left: 14px; min-width: 0; word-wrap: break-word; }
.dr-event-label { font-weight: 700; font-size: 14px; margin-bottom: 5px; color: #f0f0f0; }
.dr-event-details { font-size: 13px; color: #a0a0a0; line-height: 1.5; margin-bottom: 6px; word-wrap: break-word; }
.dr-event-visual { font-size: 12px; color: #4ade80; background: #052e16; padding: 5px 9px; border-radius: 4px; margin-top: 5px; display: inline-block; }
.dr-event-protip { font-size: 12px; color: #c4b5fd; background: #1e1030; padding: 7px 11px; border-radius: 6px; margin-top: 7px; border-left: 2px solid #7c3aed; }
.dr-event-temp { font-size: 12px; color: #f87171; margin-top: 5px; font-weight: 600; }
.dr-plan-section { padding: 14px 18px; border-top: 1px solid #2a2a2a; flex-shrink: 0; }
.dr-section-title { font-weight: 700; font-size: 13px; margin-bottom: 8px; color: #DEA51B; }
.dr-plan-section ul { margin: 0; padding-left: 20px; font-size: 12px; color: #a0a0a0; line-height: 1.7; }
.dr-plan-section ul li { margin-bottom: 5px; }
.dr-plan-warnings { background: #221200; border-left: 3px solid #f59e0b; }
.dr-bake-plan-cta { margin: 14px 18px 18px; padding: 10px 18px; background: #DEA51B; color: #1A1A1A; border: none; border-radius: 8px; font-weight: 700; cursor: pointer; width: calc(100% - 36px); font-size: 13px; font-family: inherit; transition: opacity 0.2s; flex-shrink: 0; }
.dr-bake-plan-cta:hover { opacity: 0.88; }

.dr-products { margin: 8px 0; width: 100%; }
.dr-product-item { background: #2a2a2a; border: 1px solid #333; border-radius: 8px; padding: 10px 12px; margin-bottom: 6px; }
.dr-product-item a { color: #DEA51B; font-weight: 600; text-decoration: none; display: block; margin-bottom: 3px; font-size: 13px; }
.dr-product-item a:hover { text-decoration: underline; }
.dr-product-reason { font-size: 12px; color: #888; line-height: 1.4; }

.dr-upsell-banner {
  background: linear-gradient(135deg, #1c1700, #2a2000);
  border: 1px solid #DEA51B; border-radius: 10px;
  padding: 12px; margin: 4px 0; position: relative; overflow: hidden;
}
.dr-upsell-badge {
  background: #DEA51B; color: #1A1A1A; font-size: 9px; font-weight: 800;
  padding: 2px 8px; border-radius: 10px; display: inline-block;
  margin-bottom: 7px; letter-spacing: 0.08em; text-transform: uppercase;
}
.dr-upsell-title { font-family: var(--dr-font-heading, 'Inter', system-ui, sans-serif); font-size: 14px; color: #f0e0a0; margin-bottom: 5px; }
.dr-upsell-body { font-size: 11px; color: #b8a060; margin-bottom: 10px; line-height: 1.5; }
.dr-upsell-actions { display: flex; gap: 7px; }
.dr-upsell-btn-primary {
  flex: 1; background: #DEA51B; color: #1A1A1A; font-size: 11px; font-weight: 700;
  padding: 7px 10px; border-radius: 6px; text-align: center; text-decoration: none; display: block;
}
.dr-upsell-btn-secondary {
  flex: 1; color: #DEA51B; font-size: 11px; padding: 7px 10px;
  border: 1px solid #DEA51B55; border-radius: 6px; text-align: center; text-decoration: none; display: block;
}

.dr-onboarding { display: flex; flex-direction: column; gap: 8px; padding: 4px 0; width: 100%; }
.dr-onboarding-question { font-size: 13px; color: #e0e0e0; font-weight: 600; margin-bottom: 6px; line-height: 1.5; }
.dr-onboarding-options { display: flex; flex-wrap: wrap; gap: 8px; }
.dr-option-btn {
  padding: 10px 18px; border: 1.5px solid #333; border-radius: 10px;
  background: #2a2a2a; color: #c0c0c0; font-size: 13px; font-weight: 500;
  cursor: pointer; transition: all 0.15s; text-align: left; line-height: 1.4;
  min-height: 44px;
}
.dr-option-btn:hover { border-color: #DEA51B88; background: #2e2800; color: #f0f0f0; }
.dr-option-btn.selected { border-color: #DEA51B; background: #DEA51B; color: #1A1A1A; }
.dr-temp-input-row { display: flex; gap: 8px; align-items: center; }
.dr-temp-input { width: 80px; background: #2a2a2a; border: 1.5px solid #333; border-radius: 8px; padding: 8px 10px; font-size: 14px; text-align: center; color: #f0f0f0; }
.dr-temp-input:focus { outline: none; border-color: #DEA51B; }
.dr-temp-unit { font-size: 14px; color: #888; }
.dr-start-btn {
  margin-top: 4px; padding: 10px 20px; background: #DEA51B; color: #1A1A1A;
  border: none; border-radius: 999px; font-size: 14px; font-weight: 700;
  cursor: pointer; transition: opacity 0.15s; align-self: flex-start; min-height: 44px;
}
.dr-start-btn:hover { opacity: 0.88; }
.dr-start-btn:disabled { background: #333; color: #666; cursor: not-allowed; }
.dr-progress-dots { display: flex; gap: 6px; margin-bottom: 12px; }
.dr-progress-dot { width: 8px; height: 8px; border-radius: 50%; background: #333; transition: background 0.2s; }
.dr-progress-dot.active { background: #DEA51B; }
.dr-progress-dot.done { background: #555; }
.dr-schedule-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.dr-date-input, .dr-time-input {
  background: #2a2a2a; border: 1.5px solid #333; border-radius: 8px;
  padding: 8px 10px; font-size: 14px; font-family: inherit; color: #f0f0f0;
}
.dr-date-input { flex: 1; min-width: 140px; }
.dr-time-input { width: 110px; }
.dr-date-input:focus, .dr-time-input:focus { outline: none; border-color: #DEA51B; }
.dr-cold-proof-toggle {
  margin-top: 12px; padding: 12px 16px; background: #0c1a2e; border: 1.5px solid #1e3a5f;
  border-radius: 10px; cursor: pointer; transition: all 0.15s; min-height: 44px;
}
.dr-cold-proof-toggle:hover { border-color: #3b82f6; }
.dr-cold-proof-toggle.active { border-color: #3b82f6; background: #0f2540; }
.dr-cold-proof-toggle-header { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 13px; color: #93c5fd; }
.dr-cold-proof-toggle-desc { font-size: 11px; color: #3b82f6; margin-top: 4px; line-height: 1.4; }
.dr-cold-proof-options { margin-top: 10px; display: flex; gap: 8px; flex-wrap: wrap; }
.dr-cold-option {
  padding: 6px 12px; border: 1.5px solid #1e3a5f; border-radius: 8px;
  background: #0c1a2e; color: #93c5fd; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.15s; min-height: 44px; display: flex; align-items: center;
}
.dr-cold-option:hover { background: #152a4a; }
.dr-cold-option.selected { background: #3b82f6; color: white; border-color: #3b82f6; }

body.dr-coach-open { overflow: hidden; }

#dr-coach-backdrop {
  display: none; position: fixed; inset: 0; z-index: 99997;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(2px);
}
body.dr-coach-open #dr-coach-backdrop { display: block; }

@media (max-width: 480px) {
  #dr-coach-modal {
    bottom: 0; right: 0; left: 0; width: 100%;
    height: 65dvh;
    max-height: 65dvh;
    border-radius: 16px 16px 0 0;
  }
  @supports not (height: 65dvh) {
    #dr-coach-modal { height: 65vh; max-height: 65vh; }
  }
  #dr-coach-header { border-radius: 16px 16px 0 0; }
  #dr-coach-footer { padding-bottom: calc(12px + env(safe-area-inset-bottom)); }
  .dr-option-btn { padding: 10px 18px; }
  .dr-timeline-event::before { display: none; }
  .dr-event-time::after { display: none; }
  .dr-event-content { padding-left: 0; }
}
`;
  document.head.appendChild(styles);

  // ============================================================================
  // HELPERS
  // ============================================================================

  function formatTime(offsetMinutes) {
    if (typeof offsetMinutes !== "number") return "";
    const hours = Math.floor(offsetMinutes / 60);
    const mins = offsetMinutes % 60;
    if (hours === 0) return mins + "min";
    if (mins === 0) return hours + "h";
    return hours + "h " + mins + "min";
  }

  function getLeavenIcon(method) {
    var icons = { sourdough_starter: "🫙", instant_yeast: "⚡", active_dry_yeast: "🧫", poolish: "💧", biga: "🍞", mixed: "🔄" };
    return icons[method] || "🍞";
  }

  function getDefaultSchedule() {
    var now = new Date();
    var tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(8, 0, 0, 0);
    return { dateStr: tomorrow.toISOString().split('T')[0], timeStr: '08:00' };
  }

  function formatScheduleDate(dateStr, timeStr) {
    var d = new Date(dateStr + 'T' + timeStr);
    var days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    var hours = d.getHours();
    var ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    var mins = d.getMinutes().toString().padStart(2, '0');
    return days[d.getDay()] + ' at ' + hours + ':' + mins + ' ' + ampm;
  }

  function getTodayStr() { return new Date().toISOString().split('T')[0]; }

  // ============================================================================
  // UI CREATION
  // ============================================================================

  var btn = document.createElement("button");
  btn.id = "dr-coach-trigger";
  btn.innerHTML = '🍞 ' + (cfg.brand || 'Dough Coach');
  document.body.appendChild(btn);

  var modal = document.createElement("div");
  modal.id = "dr-coach-modal";
  modal.style.display = "none";
  modal.innerHTML =
    '<div id="dr-coach-header">' +
      '<div class="dr-coach-dot"></div>' +
      '<div id="dr-coach-title">' + (cfg.brand || 'Dough Coach') + '</div>' +
      '<div id="dr-coach-label">AI \u00b7 Free</div>' +
      '<button id="dr-coach-close" aria-label="Close">\u00d7</button>' +
    '</div>' +
    '<div id="dr-coach-messages"></div>' +
    '<div id="dr-coach-footer">' +
      '<input id="dr-coach-input" placeholder="Ask about sourdough, timing, technique\u2026" />' +
      '<button id="dr-coach-send" type="button">\u2191</button>' +
    '</div>';
  document.body.appendChild(modal);

  var messagesEl = modal.querySelector("#dr-coach-messages");

  function shouldAutoScroll() {
    var threshold = 80;
    return messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight < threshold;
  }

  function scrollToBottom() {
    requestAnimationFrame(function() {
      if (shouldAutoScroll()) {
        messagesEl.scrollTop = messagesEl.scrollHeight;
      }
    });
  }

  var inputEl = modal.querySelector("#dr-coach-input");
  var sendEl = modal.querySelector("#dr-coach-send");
  var closeEl = modal.querySelector("#dr-coach-close");

  // ============================================================================
  // MESSAGE RENDERING
  // ============================================================================

  function addMsg(role, text) {
    var div = document.createElement("div");
    div.className = "dr-msg " + (role === "user" ? "dr-user" : "dr-assistant");
    var content = document.createElement("div");
    content.className = "dr-msg-content";
    content.textContent = text;
    div.appendChild(content);
    messagesEl.appendChild(div);
    scrollToBottom();
  }

  function addLoadingIndicator() {
    var div = document.createElement("div");
    div.className = "dr-msg dr-assistant";
    div.id = "dr-loading-indicator";
    var content = document.createElement("div");
    content.className = "dr-msg-content";
    content.innerHTML = '<div class="dr-typing"><span></span><span></span><span></span></div>';
    div.appendChild(content);
    messagesEl.appendChild(div);
    scrollToBottom();
  }

  function removeLoadingIndicator() {
    var loader = document.getElementById("dr-loading-indicator");
    if (loader) loader.remove();
  }

  function renderBakePlan(planData) {
    if (!planData || !planData.events || !Array.isArray(planData.events) || planData.events.length === 0) {
      addMsg('assistant', '⚠️ Error: Could not generate timeline. Please try again.');
      return;
    }

    var wrap = document.createElement("div");
    wrap.className = "dr-bake-plan";

    // Header
    var header = document.createElement('div');
    header.className = 'dr-bake-plan-header';
    var icon = planData.leavening_method ? getLeavenIcon(planData.leavening_method) : '📅';
    var title = document.createElement('div');
    title.className = 'dr-plan-title';
    title.textContent = icon + ' ' + (planData.title || 'Your Timeline');
    header.appendChild(title);

    if (planData.leavening_method || planData.difficulty || planData.total_time_hours) {
      var meta = document.createElement('div');
      meta.className = 'dr-plan-meta';
      if (planData.leavening_method) { var t = document.createElement('span'); t.className = 'dr-meta-tag'; t.textContent = planData.leavening_method.replace(/_/g, ' '); meta.appendChild(t); }
      if (planData.difficulty) { var t = document.createElement('span'); t.className = 'dr-meta-tag'; t.textContent = planData.difficulty; meta.appendChild(t); }
      if (planData.total_time_hours) { var t = document.createElement('span'); t.className = 'dr-meta-tag'; t.textContent = '⏱️ ' + planData.total_time_hours + 'h total'; meta.appendChild(t); }
      if (planData.cold_proof_included) { var t = document.createElement('span'); t.className = 'dr-meta-tag'; t.style.background = 'rgba(59,130,246,0.3)'; t.textContent = '❄️ ' + (planData.cold_proof_hours || '') + 'h cold proof'; meta.appendChild(t); }
      header.appendChild(meta);
    }
    wrap.appendChild(header);

    // Schedule summary
    if (planData.schedule_summary) {
      var summary = document.createElement('div');
      summary.className = 'dr-schedule-summary';
      summary.innerHTML = '<span class="dr-schedule-icon">📅</span><span class="dr-schedule-text">' + planData.schedule_summary + '</span>';
      wrap.appendChild(summary);
    }

    // Cold proof badge
    if (planData.cold_proof_included && planData.cold_proof_hours) {
      var badge = document.createElement('div');
      badge.className = 'dr-cold-proof-badge';
      badge.innerHTML = '<span class="dr-cold-icon">❄️</span><span>' + planData.cold_proof_hours + '-hour cold proof' + (planData.cold_proof_stage ? ' (' + planData.cold_proof_stage.replace(/_/g, ' ') + ')' : '') + ' — develops deeper flavor and better structure</span>';
      wrap.appendChild(badge);
    }

    // Ingredients
    if (planData.ingredients) {
      var ingSection = document.createElement('div');
      ingSection.className = 'dr-plan-ingredients';
      var ingTitle = document.createElement('div');
      ingTitle.className = 'dr-ing-title';
      ingTitle.textContent = '📋 Ingredients';
      ingSection.appendChild(ingTitle);
      var ingList = document.createElement('div');
      ingList.className = 'dr-ing-list';
      var ing = planData.ingredients;
      if (ing.flour_g) { var item = document.createElement('div'); item.textContent = 'Flour: ' + ing.flour_g + 'g'; ingList.appendChild(item); }
      if (ing.water_g) { var item = document.createElement('div'); item.textContent = 'Water: ' + ing.water_g + 'g' + (ing.hydration_percent ? ' (' + ing.hydration_percent + '% hydration)' : ''); ingList.appendChild(item); }
      if (ing.salt_g) { var item = document.createElement('div'); item.textContent = 'Salt: ' + ing.salt_g + 'g'; ingList.appendChild(item); }
      if (ing.starter_g) { var item = document.createElement('div'); item.textContent = 'Sourdough Starter: ' + ing.starter_g + 'g'; ingList.appendChild(item); }
      if (ing.yeast_g) { var item = document.createElement('div'); item.textContent = 'Yeast: ' + ing.yeast_g + 'g'; ingList.appendChild(item); }
      if (ing.other_ingredients && Array.isArray(ing.other_ingredients)) { ing.other_ingredients.forEach(function(oi) { var item = document.createElement('div'); item.textContent = oi.name + ': ' + oi.amount; ingList.appendChild(item); }); }
      ingSection.appendChild(ingList);
      wrap.appendChild(ingSection);
    }

    // Assumptions
    if (planData.assumptions && planData.assumptions.length) {
      var assumptions = document.createElement('div');
      assumptions.className = 'dr-bake-plan-assumptions';
      assumptions.innerHTML = '<strong>⚠️ Important:</strong> ' + planData.assumptions.join("; ");
      wrap.appendChild(assumptions);
    }

    // Timeline
    var timeline = document.createElement('div');
    timeline.className = 'dr-bake-plan-timeline';
    planData.events.forEach(function(ev) {
      var eventDiv = document.createElement('div');
      eventDiv.className = 'dr-timeline-event';
      if (ev.is_fridge_step) eventDiv.classList.add('dr-fridge-step');

      var timeDiv = document.createElement('div');
      timeDiv.className = 'dr-event-time';
      if (ev.clock_time) {
        timeDiv.innerHTML = '<span class="dr-clock-time">' + ev.clock_time + '</span><span class="dr-offset-time">' + formatTime(ev.offset_minutes) + '</span>';
      } else {
        timeDiv.textContent = formatTime(ev.offset_minutes);
      }
      eventDiv.appendChild(timeDiv);

      var contentDiv = document.createElement('div');
      contentDiv.className = 'dr-event-content';
      var label = document.createElement('div');
      label.className = 'dr-event-label';
      label.textContent = ev.is_fridge_step ? '❄️ ' + ev.label : ev.label;
      contentDiv.appendChild(label);
      var details = document.createElement('div');
      details.className = 'dr-event-details';
      details.textContent = ev.details;
      contentDiv.appendChild(details);
      if (ev.visual_cues) { var v = document.createElement('div'); v.className = 'dr-event-visual'; v.textContent = '👁️ Look for: ' + ev.visual_cues; contentDiv.appendChild(v); }
      if (ev.pro_tip) { var p = document.createElement('div'); p.className = 'dr-event-protip'; p.textContent = '💡 Pro tip: ' + ev.pro_tip; contentDiv.appendChild(p); }
      if (ev.dough_temp_target) { var t = document.createElement('div'); t.className = 'dr-event-temp'; t.textContent = '🌡️ Target: ' + ev.dough_temp_target + '°C'; contentDiv.appendChild(t); }
      eventDiv.appendChild(contentDiv);
      timeline.appendChild(eventDiv);
    });
    wrap.appendChild(timeline);

    // Success indicators
    if (planData.success_indicators && planData.success_indicators.length) {
      var section = document.createElement('div');
      section.className = 'dr-plan-section';
      section.innerHTML = '<div class="dr-section-title">✅ Success Indicators</div><ul>' + planData.success_indicators.map(function(s){return '<li>'+s+'</li>';}).join('') + '</ul>';
      wrap.appendChild(section);
    }

    // Common mistakes
    if (planData.common_mistakes && planData.common_mistakes.length) {
      var section = document.createElement('div');
      section.className = 'dr-plan-section dr-plan-warnings';
      section.innerHTML = '<div class="dr-section-title">⚠️ Common Mistakes to Avoid</div><ul>' + planData.common_mistakes.map(function(m){return '<li>'+m+'</li>';}).join('') + '</ul>';
      wrap.appendChild(section);
    }

    // Temperature note
    if (planData.temperature_adjustments) {
      var section = document.createElement('div');
      section.className = 'dr-plan-section';
      section.innerHTML = '<div class="dr-section-title">🌡️ Temperature Note</div><p style="font-size:13px;color:#4b5563;line-height:1.6;margin:0;">' + planData.temperature_adjustments + '</p>';
      wrap.appendChild(section);
    }

    // CTA
    var ctaBtn = document.createElement('a');
    ctaBtn.className = 'dr-bake-plan-cta';
    ctaBtn.href = 'https://doughrise.store/pages/coach';
    ctaBtn.target = '_blank';
    ctaBtn.rel = 'noopener';
    ctaBtn.textContent = planData.cold_proof_included
      ? '⭐ Save this plan & get step reminders — Upgrade to Coach'
      : '⭐ Get Coach — personalise this plan with your bake history →';
    wrap.appendChild(ctaBtn);

    messagesEl.appendChild(wrap);
    scrollToBottom();
  }

  function renderUpgradeCard() {
    if (sessionStorage.getItem('drCoachUpgradeShown')) return;
    sessionStorage.setItem('drCoachUpgradeShown', '1');

    var wrap = document.createElement('div');
    wrap.className = 'dr-msg dr-assistant';
    var inner = document.createElement('div');
    inner.className = 'dr-upsell-banner';
    inner.innerHTML =
      '<div class="dr-upsell-badge">Coach Plan</div>' +
      '<div class="dr-upsell-title">I can do so much more with your data</div>' +
      '<div class="dr-upsell-body">Your actual bake logs, starter history, and saved recipes \u2014 analysed together to give you a real, personalised answer.</div>' +
      '<div class="dr-upsell-actions">' +
        '<a href="https://doughrise.store/pages/coach" class="dr-upsell-btn-primary" target="_blank">Upgrade Now</a>' +
        '<a href="https://coach.doughrise.store" class="dr-upsell-btn-secondary" target="_blank">Try Coach Free</a>' +
      '</div>';
    wrap.appendChild(inner);
    messagesEl.appendChild(wrap);
    scrollToBottom();
  }

  function renderProducts(products) {
    if (!products || products.length === 0) return;
    var div = document.createElement("div");
    div.className = "dr-products";
    products.forEach(function(product) {
      var item = document.createElement("div");
      item.className = "dr-product-item";
      item.innerHTML = '<a href="' + product.url + '" target="_blank">' + product.title + '</a>' + (product.reason ? '<div class="dr-product-reason">' + product.reason + '</div>' : '');
      div.appendChild(item);
    });
    messagesEl.appendChild(div);
    scrollToBottom();
  }

  // ============================================================================
  // ONBOARDING FLOW - 5 steps (bake type, quantity, leavening, schedule, temp)
  // ============================================================================

  var onboarding = {
    step: 0,
    answers: { bakeType: null, quantity: null, leavening: null, schedule: null, temperature: null }
  };

  var BAKE_OPTIONS = [
    { label: '🍕 Pizza', value: 'pizza', quantityLabel: 'How many pizzas?', quantities: [
      { label: '1 pizza', value: 1, note: '~166g flour' },
      { label: '2 pizzas', value: 2, note: '~330g flour' },
      { label: '4 pizzas', value: 4, note: '~665g flour' },
      { label: '6 pizzas', value: 6, note: '~1kg flour' },
    ]},
    { label: '🍞 Sourdough Bread', value: 'sourdough bread', quantityLabel: 'How many loaves?', quantities: [
      { label: '1 loaf', value: 1, note: '~450g flour, feeds 4' },
      { label: '2 loaves', value: 2, note: '~900g flour, feeds 8' },
      { label: '3 loaves', value: 3, note: '~1.35kg flour' },
    ]},
    { label: '🥖 Focaccia', value: 'focaccia', quantityLabel: 'What size tray?', quantities: [
      { label: 'Small (20×20cm)', value: 1, note: '~250g flour, serves 4' },
      { label: 'Medium (30×20cm)', value: 1.5, note: '~375g flour, serves 6' },
      { label: 'Large (40×30cm)', value: 2, note: '~500g flour, serves 10' },
    ]},
    { label: '🥨 Bagels', value: 'bagels', quantityLabel: 'How many bagels?', quantities: [
      { label: '4 bagels', value: 4, note: '~400g flour' },
      { label: '6 bagels', value: 6, note: '~600g flour' },
      { label: '12 bagels', value: 12, note: '~1.2kg flour' },
    ]},
    { label: '🥐 Croissants', value: 'croissants', quantityLabel: 'How many croissants?', quantities: [
      { label: '6 croissants', value: 6, note: '~300g flour' },
      { label: '12 croissants', value: 12, note: '~600g flour' },
    ]},
    { label: '🍔 Burger Buns', value: 'burger buns', quantityLabel: 'How many buns?', quantities: [
      { label: '4 buns', value: 4, note: '~400g flour' },
      { label: '6 buns', value: 6, note: '~600g flour' },
      { label: '8 buns', value: 8, note: '~800g flour' },
    ]},
  ];

  var LEAVENING_OPTIONS = {
    'pizza': [
      { label: '🫙 Sourdough Starter', value: 'sourdough starter', note: '12-24h, complex flavour' },
      { label: '⚡ Instant Yeast', value: 'instant yeast', note: '2-3h, great for same day' },
      { label: '💧 Poolish', value: 'poolish', note: '12-18h, airy & flavourful' },
    ],
    'sourdough bread': [ { label: '🫙 Sourdough Starter', value: 'sourdough starter', note: 'Classic, 12-24h' } ],
    'focaccia': [
      { label: '🫙 Sourdough Starter', value: 'sourdough starter', note: '8-12h, extra flavour' },
      { label: '⚡ Instant Yeast', value: 'instant yeast', note: '3-4h, light & airy' },
    ],
    'bagels': [
      { label: '⚡ Instant Yeast', value: 'instant yeast', note: 'Traditional, chewy texture' },
      { label: '🫙 Sourdough Starter', value: 'sourdough starter', note: 'Tangy sourdough bagels' },
    ],
    'croissants': [ { label: '⚡ Instant Yeast', value: 'instant yeast', note: 'Traditional laminated dough' } ],
    'burger buns': [
      { label: '⚡ Instant Yeast', value: 'instant yeast', note: 'Soft & fluffy, 2-3h' },
      { label: '🫙 Sourdough Starter', value: 'sourdough starter', note: 'Flavourful, 8-12h' },
    ],
  };

  var TOTAL_STEPS = 5;

  function makeProgressDots(activeStep) {
    var html = '<div class="dr-progress-dots">';
    for (var i = 0; i < TOTAL_STEPS; i++) {
      html += '<div class="dr-progress-dot ' + (i < activeStep ? 'done' : (i === activeStep ? 'active' : '')) + '"></div>';
    }
    return html + '</div>';
  }

  function renderOnboarding() {
    messagesEl.innerHTML = '';
    var wrap = document.createElement('div');
    wrap.className = 'dr-msg dr-assistant';
    var content = document.createElement('div');
    content.className = 'dr-msg-content';

    if (onboarding.step === 0) {
      content.innerHTML = '<div class="dr-onboarding">' + makeProgressDots(0) + '<div class="dr-onboarding-question">👋 Welcome! What would you like to bake today?</div><div class="dr-onboarding-options" id="dr-bake-options"></div></div>';
      wrap.appendChild(content);
      messagesEl.appendChild(wrap);
      BAKE_OPTIONS.forEach(function(opt) {
        var b = document.createElement('button');
        b.className = 'dr-option-btn';
        b.textContent = opt.label;
        b.addEventListener('click', function() { onboarding.answers.bakeType = opt; onboarding.step = 1; renderOnboarding(); });
        document.getElementById('dr-bake-options').appendChild(b);
      });

    } else if (onboarding.step === 1) {
      var bake = onboarding.answers.bakeType;
      content.innerHTML = '<div class="dr-onboarding">' + makeProgressDots(1) + '<div class="dr-onboarding-question">' + bake.quantityLabel + '</div><div class="dr-onboarding-options" id="dr-qty-options"></div></div>';
      wrap.appendChild(content);
      messagesEl.appendChild(wrap);
      bake.quantities.forEach(function(q) {
        var b = document.createElement('button');
        b.className = 'dr-option-btn';
        b.innerHTML = q.label + ' <span style="font-size:11px;opacity:0.65;display:block;">' + q.note + '</span>';
        b.style.textAlign = 'left';
        b.addEventListener('click', function() { onboarding.answers.quantity = q; onboarding.step = 2; renderOnboarding(); });
        document.getElementById('dr-qty-options').appendChild(b);
      });

    } else if (onboarding.step === 2) {
      var bakeKey = onboarding.answers.bakeType.value;
      var options = LEAVENING_OPTIONS[bakeKey] || LEAVENING_OPTIONS['pizza'];
      content.innerHTML = '<div class="dr-onboarding">' + makeProgressDots(2) + '<div class="dr-onboarding-question">What will you use to leaven your dough?</div><div class="dr-onboarding-options" id="dr-leaven-options"></div></div>';
      wrap.appendChild(content);
      messagesEl.appendChild(wrap);
      options.forEach(function(opt) {
        var b = document.createElement('button');
        b.className = 'dr-option-btn';
        b.innerHTML = opt.label + ' <span style="font-size:11px;opacity:0.65;display:block;">' + opt.note + '</span>';
        b.style.textAlign = 'left';
        b.addEventListener('click', function() { onboarding.answers.leavening = opt; onboarding.step = 3; renderOnboarding(); });
        document.getElementById('dr-leaven-options').appendChild(b);
      });

    } else if (onboarding.step === 3) {
      // SCHEDULE STEP
      var defaults = getDefaultSchedule();
      var leavening = onboarding.answers.leavening.value;
      var showColdProof = leavening.indexOf('sourdough') >= 0 || leavening === 'poolish';

      var coldToggleHTML = showColdProof ? '<div class="dr-cold-proof-toggle" id="dr-cold-toggle"><div class="dr-cold-proof-toggle-header"><span>❄️</span><span>Add a cold proof in the fridge?</span></div><div class="dr-cold-proof-toggle-desc">Long, slow proofing (12-48h) develops deeper flavour, better texture, and lets you bake on YOUR schedule</div><div class="dr-cold-proof-options" id="dr-cold-options" style="display:none;"></div></div>' : '';

      content.innerHTML = '<div class="dr-onboarding">' + makeProgressDots(3) +
        '<div class="dr-onboarding-question">📅 When would you like your ' + onboarding.answers.bakeType.value + ' ready to eat?<div style="font-size:12px;font-weight:400;color:#6b7280;margin-top:4px;">We\'ll work backwards to tell you exactly when to start</div></div>' +
        '<div class="dr-schedule-row"><input type="date" class="dr-date-input" id="dr-sched-date" value="' + defaults.dateStr + '" min="' + getTodayStr() + '" /><input type="time" class="dr-time-input" id="dr-sched-time" value="' + defaults.timeStr + '" /></div>' +
        '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;" id="dr-quick-times"></div>' +
        coldToggleHTML +
        '<button class="dr-start-btn" id="dr-sched-next">Next →</button></div>';
      wrap.appendChild(content);
      messagesEl.appendChild(wrap);

      // Quick time buttons
      var now = new Date();
      var tom = new Date(now); tom.setDate(tom.getDate()+1);
      var tomStr = tom.toISOString().split('T')[0];
      var satDate = new Date(now);
      var dow = satDate.getDay();
      satDate.setDate(satDate.getDate() + ((6 - dow + 7) % 7 || 7));
      var satStr = satDate.toISOString().split('T')[0];

      var quickTimes = [
        { label: '🌅 Tomorrow 8am', date: tomStr, time: '08:00' },
        { label: '🍽️ Tomorrow noon', date: tomStr, time: '12:00' },
        { label: '🌙 Tomorrow 6pm', date: tomStr, time: '18:00' },
        { label: '📅 This weekend', date: satStr, time: '10:00' },
      ];

      var qtContainer = document.getElementById('dr-quick-times');
      quickTimes.forEach(function(qt) {
        var b = document.createElement('button');
        b.className = 'dr-option-btn';
        b.textContent = qt.label;
        b.style.fontSize = '12px';
        b.style.padding = '6px 10px';
        b.addEventListener('click', function() {
          document.getElementById('dr-sched-date').value = qt.date;
          document.getElementById('dr-sched-time').value = qt.time;
          qtContainer.querySelectorAll('.dr-option-btn').forEach(function(x){x.classList.remove('selected');});
          b.classList.add('selected');
        });
        qtContainer.appendChild(b);
      });

      // Cold proof
      var coldProofEnabled = false;
      var coldProofHours = null;

      if (showColdProof) {
        var toggle = document.getElementById('dr-cold-toggle');
        var optionsWrap = document.getElementById('dr-cold-options');

        toggle.addEventListener('click', function(e) {
          if (e.target.classList.contains('dr-cold-option')) return;
          coldProofEnabled = !coldProofEnabled;
          toggle.classList.toggle('active', coldProofEnabled);
          optionsWrap.style.display = coldProofEnabled ? 'flex' : 'none';
          if (!coldProofEnabled) { coldProofHours = null; optionsWrap.querySelectorAll('.dr-cold-option').forEach(function(x){x.classList.remove('selected');}); }
        });

        [
          { label: '12h overnight', hours: 12 },
          { label: '24h (recommended)', hours: 24 },
          { label: '36h', hours: 36 },
          { label: '48h max flavour', hours: 48 },
        ].forEach(function(co) {
          var b = document.createElement('button');
          b.className = 'dr-cold-option';
          b.textContent = co.label;
          b.addEventListener('click', function(e) {
            e.stopPropagation();
            coldProofHours = co.hours;
            optionsWrap.querySelectorAll('.dr-cold-option').forEach(function(x){x.classList.remove('selected');});
            b.classList.add('selected');
          });
          optionsWrap.appendChild(b);
        });
      }

      document.getElementById('dr-sched-next').addEventListener('click', function() {
        var dateVal = document.getElementById('dr-sched-date').value;
        var timeVal = document.getElementById('dr-sched-time').value;
        if (!dateVal || !timeVal) { alert('Please select a date and time'); return; }
        onboarding.answers.schedule = { dateStr: dateVal, timeStr: timeVal, coldProof: coldProofEnabled, coldProofHours: coldProofHours };
        onboarding.step = 4;
        renderOnboarding();
      });

    } else if (onboarding.step === 4) {
      // TEMPERATURE STEP
      content.innerHTML = '<div class="dr-onboarding">' + makeProgressDots(4) +
        '<div class="dr-onboarding-question">What\'s the temperature in your kitchen today?<div style="font-size:12px;font-weight:400;color:#6b7280;margin-top:4px;">This affects fermentation speed and timing</div></div>' +
        '<div class="dr-temp-input-row"><input class="dr-temp-input" id="dr-temp-value" type="number" min="15" max="40" placeholder="22" /><span class="dr-temp-unit">°C</span></div>' +
        '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;" id="dr-temp-shortcuts"></div>' +
        '<button class="dr-start-btn" id="dr-start-chat" disabled>Let\'s bake! →</button></div>';
      wrap.appendChild(content);
      messagesEl.appendChild(wrap);

      [
        { label: '❄️ Cold (18°C)', value: 18, hint: 'Slow fermentation' },
        { label: '😊 Ideal (21°C)', value: 21, hint: 'Standard timing' },
        { label: '☀️ Warm (25°C)', value: 25, hint: 'Slightly faster' },
        { label: '🔥 Hot (30°C)', value: 30, hint: 'Monitor closely' },
      ].forEach(function(s) {
        var b = document.createElement('button');
        b.className = 'dr-option-btn';
        b.innerHTML = s.label + '<span style="font-size:11px;opacity:0.65;display:block;">' + s.hint + '</span>';
        b.style.textAlign = 'left';
        b.addEventListener('click', function() {
          document.getElementById('dr-temp-value').value = s.value;
          document.getElementById('dr-start-chat').disabled = false;
          document.querySelectorAll('#dr-temp-shortcuts .dr-option-btn').forEach(function(x){x.classList.remove('selected');});
          b.classList.add('selected');
        });
        document.getElementById('dr-temp-shortcuts').appendChild(b);
      });

      document.getElementById('dr-temp-value').addEventListener('input', function(e) {
        document.getElementById('dr-start-chat').disabled = !e.target.value;
      });

      document.getElementById('dr-start-chat').addEventListener('click', function() {
        var temp = document.getElementById('dr-temp-value').value;
        if (!temp) return;
        onboarding.answers.temperature = parseInt(temp);
        startChatWithContext();
      });
    }
  }

  function startChatWithContext() {
    var bakeType = onboarding.answers.bakeType;
    var quantity = onboarding.answers.quantity;
    var leavening = onboarding.answers.leavening;
    var schedule = onboarding.answers.schedule;
    var temperature = onboarding.answers.temperature;

    messagesEl.innerHTML = '';

    var readyTimeStr = formatScheduleDate(schedule.dateStr, schedule.timeStr);
    var coldProofStr = schedule.coldProof && schedule.coldProofHours
      ? " I'd like a " + schedule.coldProofHours + "-hour cold proof in the fridge for extra flavour."
      : '';

    var summaryText = 'I want to make ' + quantity.label.toLowerCase() + ' using ' + leavening.value + '. I want it ready by ' + readyTimeStr + '.' + coldProofStr + ' My kitchen is ' + temperature + '°C today.';
    addMsg('user', summaryText);

    var prompt = 'Generate a detailed baking plan for the following:\n' +
      '- What: ' + bakeType.value + '\n' +
      '- Quantity: ' + quantity.label + ' (' + quantity.note + ')\n' +
      '- Leavening: ' + leavening.value + '\n' +
      '- Target ready time: ' + readyTimeStr + ' (' + schedule.dateStr + ' ' + schedule.timeStr + ')\n' +
      '- Kitchen temperature: ' + temperature + '°C\n' +
      (schedule.coldProof && schedule.coldProofHours
        ? '- Cold proof requested: YES, ' + schedule.coldProofHours + ' hours in the fridge\n- Please work backwards from the target ready time, including the cold proof period\n'
        : '- No cold proof requested, but if you think one would benefit the schedule or flavour, suggest it\n') +
      '\nIMPORTANT: Work backwards from the target ready time of ' + readyTimeStr + '. Calculate when the user needs to START and include clock_time for every step. Include a schedule_summary showing the multi-day flow (e.g., "Start Thursday 8pm → Fridge Friday → Bake Saturday 8am").\n\nPlease calculate all ingredient amounts precisely for this exact quantity using baker\'s percentages, and explain briefly why you\'ve chosen those amounts.';

    sendMessage(prompt, 'bake_plan');
  }

  var setOpen = function(open) {
    modal.style.display = open ? "flex" : "none";
    btn.style.display = open ? "none" : "flex";
    if (open) {
      document.body.classList.add('dr-coach-open');
      if (messagesEl.childElementCount === 0) {
        renderOnboarding();
      }
    } else {
      document.body.classList.remove('dr-coach-open');
    }
  };

  var backdrop = document.createElement("div");
  backdrop.id = "dr-coach-backdrop";
  document.body.appendChild(backdrop);
  backdrop.addEventListener("click", function() { setOpen(false); });

  btn.addEventListener("click", function() { setOpen(true); });
  closeEl.addEventListener("click", function() { setOpen(false); });

  function sendMessage(text, intent) {
    if (isLoading) return;
    isLoading = true;
    sendEl.disabled = true;
    addLoadingIndicator();

    var body = { sessionId: sessionId, message: text };
    if (intent) body.intent = intent;

    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    })
    .then(function(res) {
      if (res.status === 429) {
        removeLoadingIndicator();
        renderUpgradeCard();
        addMsg("assistant", "You've reached the free daily limit. Upgrade to DoughRise Coach for unlimited access.");
        isLoading = false;
        sendEl.disabled = false;
        return null;
      }
      return res.text();
    })
    .then(function(raw) {
      if (raw === null) return;
      var data;
      try { data = JSON.parse(raw); } catch(e) { data = { reply: "Sorry, I had trouble understanding the response. Please try again." }; }
      removeLoadingIndicator();
      if (data.reply) addMsg("assistant", data.reply);
      if (data.bake_plan) {
        try { renderBakePlan(data.bake_plan); } catch(e) { console.error('renderBakePlan error:', e); addMsg('assistant', '⚠️ Could not render the bake plan. Please try again.'); }
        renderUpgradeCard();
      }
      if (data.products) renderProducts(data.products);
      if (data.pro_suggestion) renderUpgradeCard();
    })
    .catch(function(e) {
      removeLoadingIndicator();
      addMsg("assistant", "Network error. Please check your connection and try again.");
      console.error("Chat error:", e);
    })
    .finally(function() {
      sendEl.disabled = false;
      isLoading = false;
      inputEl.focus();
    });
  }

  function send() {
    if (isLoading) return;
    var text = inputEl.value.trim();
    if (!text) return;
    inputEl.value = "";
    addMsg("user", text);
    userMessageCount++;
    if (userMessageCount === 5) renderUpgradeCard();
    sendMessage(text);
  }

  sendEl.addEventListener("click", send);
  inputEl.addEventListener("keydown", function(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  });

  console.log('Doughrise Coach initialized successfully');
})();